import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import LeafIcon from '../../assets/leaf-icon.svg';
import Slide1 from './components/Slide1';
import Slide2 from './components/Slide2';
import Slide3 from './components/Slide3';
import Slide4 from './components/Slide4';
import Slide5 from './components/Slide5';
import Slide6 from './components/Slide6';
import Slide7 from './components/Slide7';

export default function WelcomeModal({ isOpen, onClose }) {
  const [currentSlide, setCurrentSlide] = useState(1);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const totalSlides = 7;
  // Handle keyboard navigation and prevent background scroll
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') previousSlide();
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      // Prevent background scrolling when modal is open
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      // Restore background scrolling when modal closes
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentSlide, isOpen, onClose]);
  const nextSlide = () => {
    if (currentSlide < totalSlides) {
      setCurrentSlide(currentSlide + 1);
      // Scroll to top of slide content
      const slideContent = document.querySelector('.slide-content');
      if (slideContent) {
        slideContent.scrollTop = 0;
      }
    } else {
      onClose();
    }
  };
  const previousSlide = () => {
    if (currentSlide > 1) {
      setCurrentSlide(currentSlide - 1);
      // Scroll to top of slide content
      const slideContent = document.querySelector('.slide-content');
      if (slideContent) {
        slideContent.scrollTop = 0;
      }
    }
  };

  // Touch handlers for swipe navigation
  const handleTouchStart = (e) => {
    // Don't start swipe tracking if touching interactive elements
    const target = e.target;
    if (target.type === 'range' || target.closest('input[type="range"]')) {
      return;
    }

    setTouchEnd(null); // Reset touchEnd
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    // Don't track movement if we didn't start tracking (e.g., on sliders)
    if (!touchStart) return;

    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    }
    if (isRightSwipe) {
      previousSlide();
    }

    // Reset touch state
    setTouchStart(null);
    setTouchEnd(null);
  };
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[781px] max-h-[90vh] overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-100 to-green-50 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <img src={LeafIcon} alt="Twig" className="w-6 h-6" />
            <span className="font-semibold text-gray-800">Welcome to Twygs</span>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        {/* Progress Dots */}
        <div className="flex justify-center items-center py-4 px-6 bg-gray-50">
          <div className="flex space-x-2">
            {[...Array(totalSlides)].map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index < currentSlide ? 'scale-110' : 'bg-gray-300'
                }`}
                style={index < currentSlide ? { backgroundColor: '#4CAF50' } : {}}
              />
            ))}
          </div>
        </div>

        {/* Slide Content */}
        <div
          className="slide-content p-6 md:p-8 overflow-y-auto max-h-[calc(90vh-200px)]"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {currentSlide === 1 && <Slide1 />}
          {currentSlide === 2 && <Slide2 />}
          {currentSlide === 3 && <Slide3 />}
          {currentSlide === 4 && <Slide4 />}
          {currentSlide === 5 && <Slide5 />}
          {currentSlide === 6 && <Slide6 />}
          {currentSlide === 7 && <Slide7 onComplete={onClose} />}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-t">
          <button
            onClick={previousSlide}
            className={`flex items-center text-gray-600 font-medium py-2 px-4 rounded-lg transition hover:bg-gray-100 ${
              currentSlide === 1 ? 'invisible' : ''
            }`}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </button>
          <button
            onClick={nextSlide}
            className="flex items-center bg-gradient-to-r from-green-600 to-green-700 text-white font-medium py-2 px-6 rounded-lg transition hover:from-green-700 hover:to-green-800 shadow-md hover:shadow-lg transform hover:scale-105"
            style={{
              background: 'linear-gradient(to right, #4CAF50, #45a049)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(to right, #45a049, #3d8b40)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(to right, #4CAF50, #45a049)';
            }}
          >
            {currentSlide === totalSlides ? 'Start Exploring' : 'Next'}
            <ChevronRight className="w-4 h-4 ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
}
